import React from "react";
import { Link } from "react-router-dom";
import { 
  ShieldCheck, Award, Users, Truck, Clock, 
  CheckCircle, ArrowRight, Phone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import VideoHero from "@/components/VideoHero";

const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_cornwall-movers/artifacts/wk29609y_1768943867734.jpg";
const TRUCK_IMAGE = "https://customer-assets.emergentagent.com/job_cornwall-movers/artifacts/z3gcuzaw_1769062270266.png";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_movewithtony/artifacts/0c98olj8_c413a017a11bcba_file_000000009aec61fba9d62adbd361f5c9_wm.png";

const AboutPage = () => {
  const values = [
    {
      icon: ShieldCheck,
      title: "Fully Insured",
      description: "Your belongings are protected. We carry comprehensive insurance for complete peace of mind."
    },
    {
      icon: Users,
      title: "Professional Team",
      description: "Our experienced crew treats every item with care, like it's their own."
    },
    {
      icon: Clock,
      title: "On-Time Service",
      description: "We respect your time. Punctual arrivals and efficient moves are our standard."
    },
    {
      icon: Award,
      title: "10+ Years Experience",
      description: "A decade of moving expertise means we know how to handle any challenge."
    }
  ];

  const stats = [
    { value: "10+", label: "Years Experience" },
    { value: "500+", label: "Happy Customers" },
    { value: "100%", label: "Insured Moves" },
    { value: "24hr", label: "Response Time" }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section with Video */}
      <VideoHero
        title="About Tony Moves It"
        subtitle="Your trusted local movers in Cornwall and SDG. Professional service with a personal touch."
      />

      {/* Story Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#0047AB] font-semibold mb-3 uppercase tracking-wide">Our Story</p>
              <h2 className="text-4xl md:text-5xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 uppercase">
                From Passion to<br />
                <span className="text-[#0047AB]">Cornwall's Choice</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Tony Moves It is proudly serving Cornwall and SDG with 
                amazing customer reviews and satisfaction. Tony Russell (owner), 
                with over 10 years of moving experience, has started his own moving business.
              </p>
              <p className="text-lg text-slate-600 mb-6">
                With 10+ years in the business, Tony and crew will get you moved 
                from A to B stress-free, guaranteed. We're a proud local company 
                dedicated to making your move fast, friendly, and completely worry-free.
              </p>
              <p className="text-lg text-slate-600 mb-8">
                We believe in treating our community like family, which means fair pricing, 
                honest service, and the professional care your belongings deserve. 
                At Tony Moves It, we take the worry out of moving so you can focus on 
                your new beginning.
              </p>
              <a href="tel:613-362-8180">
                <Button className="bg-[#0047AB] hover:bg-[#003380] text-white font-bold">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Tony: 613-362-8180
                </Button>
              </a>
            </div>
            <div className="relative">
              <img 
                src={ABOUT_IMAGE} 
                alt="Tony Moves It Team" 
                className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-slate-600 text-white p-6 rounded-xl shadow-lg">
                <p className="text-4xl font-bold font-['Barlow_Condensed']">10+</p>
                <p className="text-sm">Years in Business</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0047AB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-5xl font-bold font-['Barlow_Condensed'] text-white mb-2">
                  {stat.value}
                </p>
                <p className="text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#0047AB] font-semibold mb-3 uppercase tracking-wide">Why Choose Us</p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Barlow_Condensed'] text-slate-900 uppercase">
              Our Commitment to You
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="bg-[#0047AB]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-[#0047AB]" />
                </div>
                <h3 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#0047AB] font-semibold mb-3 uppercase tracking-wide">Service Area</p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Barlow_Condensed'] text-slate-900 uppercase mb-6">
              Proudly Serving Cornwall & SDG
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We know the local area inside and out. From downtown Cornwall to the rural 
              communities of Stormont, Dundas, and Glengarry, we've got you covered.
            </p>
          </div>
          <div className="bg-slate-100 rounded-2xl p-8">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h3 className="font-bold text-slate-900 mb-3">Cornwall</h3>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0047AB]" />
                    Downtown Cornwall
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0047AB]" />
                    East End
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0047AB]" />
                    West End
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-3">Stormont</h3>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0047AB]" />
                    Long Sault
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0047AB]" />
                    Ingleside
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0047AB]" />
                    Morrisburg
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-3">Dundas & Glengarry</h3>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0047AB]" />
                    Alexandria
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0047AB]" />
                    Lancaster
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0047AB]" />
                    Winchester
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold font-['Barlow_Condensed'] text-white uppercase mb-6">
            Ready to Experience the Difference?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Get your free quote today or book your move directly. 
            Let Tony and the team take care of the heavy lifting.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote" data-testid="about-quote-btn">
              <Button className="bg-white text-[#0047AB] hover:bg-slate-100 font-bold text-lg py-6 px-8 w-full sm:w-auto">
                Get Free Quote
              </Button>
            </Link>
            <Link to="/booking" data-testid="about-book-btn">
              <Button className="bg-slate-600 hover:bg-slate-700 text-white font-bold text-lg py-6 px-8 w-full sm:w-auto">
                Book Your Move
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
